import { makeAutoObservable, action } from "mobx";
import apiService from "../services/apiService";
import { logApiError } from "../utils/logApiError";

export class AdminStore {
  modules = [];
  firmWares = [];
  error: string | null = null;
  adminModule = {};
  AC = [];
  updatingType: "ESP" | "STM" | "AC_control" | null = null;
  updatingModuleId: string | null = null;
  lastUpdateStartedAt: string | null = null;
  updateLogs = [];
  updateStatus: Record<
    "ESP" | "STM" | "AC_control",
    "idle" | "updating" | "complete" | "failed"
  > = {
    ESP: "idle",
    STM: "idle",
    AC_control: "idle",
  };

  private STORAGE_KEY = "adminStoreSession";

  constructor() {
    makeAutoObservable(this);
    this.loadFromLocalStorage();
  }
  @action
  setError(error: string | null) {
    this.error = error;
  }

  @action
  setFirmWares(firmWares: []) {
    this.firmWares = firmWares;
  }
  @action
  setModules(modules: []) {
    this.modules = modules;
  }

  @action
  setAdminModule(module) {
    this.adminModule = module;

    // 🔽 Сбрасываем статусы при смене модуля
    this.updateStatus = {
      ESP: "idle",
      STM: "idle",
      AC_control: "idle",
    };

    this.saveToLocalStorage();

    // `ОБН.4` (`aq-130` (б)): у ММБ станций нет — список станций не запрашиваем
    // вовсе. Неизвестный тип оставляем как прежде (три блока, запрос есть).
    const deviceType = String(module?.device_type || "").toLowerCase();

    if (deviceType !== "mmb") {
      this.fetchAC(module.id);
    } else {
      this.AC = [];
    }
  }

  @action
  setAC(AC) {
    this.AC = AC;
  }

  @action
  clearSession() {
    localStorage.removeItem(this.STORAGE_KEY);
    // и сбросить нужные поля
  }

  @action
  setUpdatingState(
    type: "ESP" | "STM" | "AC_control",
    moduleId: string,
    startedAt: string,
  ) {
    this.updatingType = type;
    this.updatingModuleId = moduleId;
    this.lastUpdateStartedAt = startedAt;
    this.saveToLocalStorage();
  }

  @action
  clearUpdatingState() {
    this.updatingType = null;
    this.updatingModuleId = null;
    this.lastUpdateStartedAt = null;
    this.saveToLocalStorage();
  }

  @action
  setUpdateStatus(
    type: "ESP" | "STM" | "AC_control",
    status: "idle" | "updating" | "complete" | "failed",
  ) {
    this.updateStatus[type] = status;
    this.saveToLocalStorage(); // можно, но не обязательно
  }

  @action
  clearUpdateStatus() {
    this.updateStatus = {
      ESP: "idle",
      STM: "idle",
      AC_control: "idle",
    };
  }

  async fetchLogs(isNewProtocol: boolean = false): Promise<string> {
    try {
      const params = isNewProtocol ? { new: true } : undefined;
      const response = await apiService.fetchEntity<{ logs: string }>("/logs", {
        params,
      });
      const logs = response.data.logs || "";
      if (!logs) return "Лог пустой";
      // Новые записи отображаются сверху.
      return logs.split("\n").reverse().join("\n");
    } catch (err: any) {
      throw new Error(err.response?.data?.message || "Ошибка загрузки логов");
    }
  }

  async fetchModules() {
    try {
      const modules = await apiService.fetchEntity(`/modules`);
      this.setModules(modules.data);
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
    }
  }
  async fetchFirmWare() {
    try {
      const versions = await apiService.fetchEntity(`/firmware`);
      this.setFirmWares(versions.data);
      this.saveToLocalStorage();
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
    }
  }

  async fetchAC(id) {
    try {
      const controls = await apiService.fetchEntity(`/Acontrols/${id}`);
      this.setAC(controls.data);
      this.saveToLocalStorage();
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
    }
  }

  async updateFirmWare(F: number, M: string, type: string) {
    console.log({ F: F, M: M, type: type });
    try {
      if (type === "AC_control") {
        type = "ACC";
      }
      const response = await apiService.POST(`/firmware`, { F, M, type });
      return response.data.message;
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
      // `ОБН.2` (`АРХ-Н22`): ошибку запуска показывает вкладка. Прежде она
      // глоталась здесь, и `catch` во вкладке не срабатывал никогда: на
      // 403/404/502 вкладка ставила «обновляется» и запускала опрос.
      throw error;
    }
  }

  async sendMesage(message: string, identifier: string) {
    try {
      const response = await apiService.POST(`/endpoint`, {
        message,
        identifier,
      });
      return response.data.message;
    } catch (error: any) {
      this.setError(error.message || "Failed to fetch logs");
    }
  }

  async deleteFirmWare(id: number) {
    try {
      await apiService.deleteEntity("/firmware", id);
      this.fetchFirmWare();
    } catch (error: any) {
      this.setError(error.message || "Failed to delete firmware");
    }
  }

  async pollFirmLogs(moduleId: string, since: string) {
    try {
      const response = await apiService.fetchEntity(
        `/pollFirmWare/${moduleId}`,
        {
          params: { since },
        },
      );
      return response.data; // массив логов
    } catch (error) {
      this.setError(error.message || "Ошибка при получении логов");
      throw error; // чтобы в компоненте можно было перехватить
    }
  }

  @action
  saveToLocalStorage() {
    try {
      const data = {
        firmWares: this.firmWares,
        adminModule: this.adminModule,
        AC: this.AC,
        updatingType: this.updatingType,
        updatingModuleId: this.updatingModuleId,
        lastUpdateStartedAt: this.lastUpdateStartedAt,
        updateLogs: this.updateLogs,
        updateStatus: this.updateStatus, // 🔽 добавь
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      logApiError("Failed to save to localStorage", e);
    }
  }

  @action
  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);

        if (data.firmWares) this.firmWares = data.firmWares;
        if (data.adminModule) this.adminModule = data.adminModule;
        if (data.AC) this.AC = data.AC;
        if (data.updatingType) this.updatingType = data.updatingType;
        if (data.updatingModuleId)
          this.updatingModuleId = data.updatingModuleId;
        if (data.lastUpdateStartedAt)
          this.lastUpdateStartedAt = data.lastUpdateStartedAt;
        if (data.updateLogs) this.updateLogs = data.updateLogs;
        if (data.updateStatus) this.updateStatus = data.updateStatus; // 🔽
      }
    } catch (e) {
      logApiError("Failed to load from localStorage", e);
    }
  }
}

export const adminStore = new AdminStore();
